<template>
  <div
    :class="[
      'pill',
      info && 'pill--info',
      success && 'pill--success',
      danger && 'pill--danger',
      warn && 'pill--warn',
      cart && 'pill--cart',
      translucid && 'pill--translucid',
      sober && 'pill--sober',
      soberBlack && 'pill--sober-black',
      black && 'pill--black',
      straightenCorner && straightenCornerValue,
      small && 'pill--small',
      soberBlackFlat && 'pill--sober-black-flat',
    ]"
  >
    <div class="pill__content">
      <div v-if="text" class="pill__text">
        {{ text }}
      </div>
      <slot v-else class="pill__text" />
    </div>
  </div>
</template>

<script>
export default {
  name: 'PillBadge',
  props: {
    text: {
      type: String,
      required: false,
      default: '',
    },
    info: {
      type: Boolean,
      required: false,
      default: false,
    },
    success: {
      type: Boolean,
      required: false,
      default: false,
    },
    danger: {
      type: Boolean,
      required: false,
      default: false,
    },
    warn: {
      type: Boolean,
      required: false,
      default: false,
    },
    cart: {
      type: Boolean,
      required: false,
      default: false,
    },
    translucid: {
      type: Boolean,
      required: false,
      default: false,
    },
    sober: {
      type: Boolean,
      required: false,
      default: false,
    },
    soberBlack: {
      type: Boolean,
      required: false,
      default: false,
    },
    black: {
      type: Boolean,
      required: false,
      default: false,
    },
    soberBlackFlat: {
      type: Boolean,
      required: false,
      default: false,
    },
    straightenCorner: {
      type: String,
      required: false,
      default: '',
    },
    small: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    straightenCornerValue() {
      const classString = 'pill--straighten-corner'
      let classImplemented = ''
      if (this.straightenCorner) {
        classImplemented = this.straightenCorner
          .split(' ')
          .map(strg => `${classString}-${strg}`)
      }
      return classImplemented
    },
  },
}
</script>

<style lang="scss" scoped>
.pill {
  $this: &;
  color: var(--charcoal);
  font-weight: 450;
  font-size: rem(12px);
  line-height: 14px;
  padding: 5px 8px;
  border-radius: 30px;
  width: fit-content;
  background-color: var(--white);
  box-shadow: var(--box-shadow-lavender);
  @include desktop {
    display: inline-flex;
  }
  &__content {
    font-family: var(--font-regular);
    white-space: nowrap;
  }
  &--info {
    background-color: var(--chetwode-blue);
  }
  &--success {
    background-color: var(--strong-light-green);
    box-shadow: var(--box-shadow-strong-light-green);
  }
  &--danger {
    background-color: var(--red-pill);
    box-shadow: var(--box-shadow-red-pill);
  }
  &--warn {
    background-color: var(--orange-f8);
    box-shadow: var(--box-shadow-orange);
  }
  &--cart {
    background: var(--purple-gradient);
    box-shadow: var(--purple-pale);
    color: var(--white);
    padding: 10px 15px;
  }
  &--translucid {
    color: var(--white);
    background: var(--glass-extra-light);
    background: linear-gradient(
      104.99deg,
      rgba(62, 63, 73, 0.7) -13.91%,
      rgba(62, 63, 73, 0) 100.48%
    );
    box-shadow: 0px 0px 16px #05499863;
    border: 1px solid var(--glass-light-gray);
  }
  &--sober {
    border: 1px solid var(--light-gray-darker);
    box-shadow: none;
    background: var(--glass-extra-light);
  }
  &--sober-black {
    background: var(--glass-mid-gray-solid);
    border-radius: 20px;
    height: 32px;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    color: var(--white);
    padding: 0;
    #{$this} {
      &__content {
        background: var(--mid-gray-gradient);
        height: 30px;
        border-radius: 20px;
        width: calc(100% - 2px);
        display: flex;
        align-items: center;
        padding: 0 5px;
        &:hover {
          background: var(--light-gray);
        }
        &:active {
          background: var(--mid-gray-gradient);
        }
      }
      &__text {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 5px;
        padding: 0 5px;
        font-size: rem(14px) !important;
        line-height: rem(21px) !important;
      }
    }
  }
  &--black {
    background: var(--glass-mid-gray);
    border-radius: 20px;
    height: 32px;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    color: var(--white);
    padding: 0;
    #{$this} {
      &__content {
        background: var(--glass-dark);
        height: 30px;
        border-radius: 20px;
        width: calc(100% - 2px);
        display: flex;
        align-items: center;
        padding: 0 5px;
      }
      &__text {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 5px;
        padding: 0 5px;
        font-size: rem(14px) !important;
        line-height: rem(21px) !important;
      }
    }
  }
  &--straighten-corner {
    &-top-left {
      border-top-left-radius: 0 !important;
    }
    &-top-right {
      border-top-right-radius: 0 !important;
    }
    &-bottom-left {
      border-bottom-left-radius: 0 !important;
    }
    &-bottom-right {
      border-bottom-right-radius: 0 !important;
    }
  }
  &--small {
    height: 24px;
    #{$this} {
      &__content {
        height: 23px;
      }
    }
  }
  &--sober-black-flat {
    display: flex;
    padding: 9px 14px;
    justify-content: flex-end;
    align-items: flex-start;
    gap: 10px;
    border-radius: 20px;
    background: var(
      --glass-dark,
      linear-gradient(
        111deg,
        rgba(18, 18, 18, 0.7) 3.86%,
        rgba(18, 18, 18, 0) 107.96%
      )
    );
    overflow: hidden;

    box-shadow: 0 0 0 1px rgba(62, 63, 73, 0.7) inset;
    box-shadow: 0 0 0 1px rgba(62, 63, 73, 0.7) inset,
      0 0 0 2px rgba(62, 63, 73, 0) inset;
    border-color: var(--tuna-gray);

    #{$this}__text {
      font-size: rem(12px);
      font-style: normal;
      font-weight: 450;
      line-height: rem(14px);
      text-transform: uppercase;
      color: var(--white);
    }
  }
}
</style>
